import React, { Fragment } from "react";
import { GetServerSideProps } from "next";
// import Head from "next/head";
import { NextSeo } from "next-seo";
import {
  Section,
  Container,
  Hero,
  Level,
  Heading,
  Columns
} from "react-bulma-components";

import {
  Field as BulmaField,
  Control,
  Label
} from "react-bulma-components/lib/components/form";

import Jobs from "../../components/jobs/Jobs";
import { db } from "../../lib/firebase";
import { PUBLISHED, JOBS_PAGE_SIZE, COLORS, colors } from "../../constants";

import CitySelect from "../../components/inputs/CitySelect";
import TagSelect from "../../components/inputs/TagSelect";
import CompanySelect from "../../components/inputs/CompanySelect";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Timestamp } from "../../lib/firebase";
import { useRouter } from "next/router";
import pickBy from "lodash/pickBy";
import Icon from "../../components/Icon";

const searchSchema = Yup.object().shape({
  city: Yup.string()
    .default(null)
    .nullable(),
  country: Yup.string()
    .default(null)
    .nullable(),
  after: Yup.number()
    .positive()
    .nullable()
    .default(null),
  tags: Yup.array(Yup.string())
    .default(null)
    .max(3)
    .nullable(),
  company: Yup.string()
    .default(null)
    .nullable()
});
const JobsIndex = ({
  jobs,
  after,
  before,
  absFirst,
  absLast,
  selectedCompany
}) => {
  const router = useRouter();
  const { tags, company, city, country } = router.query;
  const currentPageSize = jobs?.length || 0;

  let hasNext,
    hasPrev = false;
  let firstJob,
    lastJob = null;

  if (currentPageSize > 0) {
    firstJob = jobs[0];
    lastJob = jobs[currentPageSize - 1];
    hasNext = Date.parse(lastJob.expiry) < Date.parse(absLast);
    hasPrev = Date.parse(firstJob.expiry) > Date.parse(absFirst);
  }

  const initialValueTags = Array.isArray(tags) ? tags : [tags];
  const validCity = city ? `in ${city}, ${country}` : "";
  const validCompany = company ? `at ${selectedCompany.name}` : "";

  return (
    <Fragment>
      <NextSeo
        title={`Search ${initialValueTags} Jobs ${validCity} ${validCompany}`}
        description="Search and browse jobs offered by companies on BountyHiring."
        titleTemplate="%s | BountyHiring"
      />
      <Section>
        <Hero color="primary" gradient>
          <Hero.Body>
            <Container>
              <Heading>Browse jobs</Heading>
              <Heading subtitle>
                Search globally for the right job for your skills
              </Heading>
            </Container>
          </Hero.Body>
        </Hero>
        <br />
        <Columns>
          <Columns.Column>
            <Formik
              initialValues={{
                ...searchSchema.default(),
                tags: initialValueTags,
                cityPick: {
                  city,
                  country
                },
                company
              }}
              validationSchema={searchSchema}
              onSubmit={values => {
                console.log(values);
              }}
              validate={async values => {
                const isValid = await searchSchema.isValid(values);
                if (isValid) {
                  const nonEmpty = pickBy(values, (val, __) => !!val);
                  delete nonEmpty.cityPick;
                  router.push({ query: nonEmpty });
                }
              }}
              validateOnChange={true}
            >
              {({ values, setFieldTouched, setFieldValue, setValues }) => (
                <Form>
                  <Columns>
                    <Columns.Column size={2}>
                      <BulmaField>
                        <Label>City</Label>
                        <Control>
                          <Field
                            name="cityPick"
                            as={CitySelect}
                            values={values}
                            setValues={setValues}
                            isCreatable={false}
                            isClearable={true}
                            setFieldTouched={setFieldTouched}
                          />
                        </Control>
                      </BulmaField>
                    </Columns.Column>
                    <Columns.Column size={2}>
                      <BulmaField>
                        <Label>Tags</Label>
                        <Control>
                          <Field
                            name="tags"
                            as={TagSelect}
                            value={initialValueTags}
                            isCreatable={false}
                            isClearable={true}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                          />
                        </Control>
                      </BulmaField>
                    </Columns.Column>
                    <Columns.Column size={2}>
                      <BulmaField>
                        <Label>Company</Label>
                        <Control>
                          <Field
                            name="company"
                            as={CompanySelect}
                            isCreatable={false}
                            isClearable={true}
                            setFieldValue={setFieldValue}
                            setFieldTouched={setFieldTouched}
                          />
                        </Control>
                      </BulmaField>
                    </Columns.Column>
                    <Columns.Column offset={3}>
                      <nav
                        className="pagination is-grouped is-pulled-right"
                        role="navigation"
                        aria-label="pagination"
                      >
                        {hasPrev && (
                          <a
                            className="pagination-previous button is-light"
                            title="This is the first page"
                            onClick={e => {
                              e.preventDefault();
                              setValues({
                                ...values,
                                before: Date.parse(firstJob.expiry),
                                after: null
                              });
                            }}
                          >
                            Previous page
                          </a>
                        )}
                        {hasNext && (
                          <a
                            className="pagination-next button is-light"
                            onClick={e => {
                              e.preventDefault();
                              setValues({
                                ...values,
                                before: null,
                                after: Date.parse(lastJob.expiry)
                              });
                            }}
                          >
                            Next page
                          </a>
                        )}
                        {/* <ul className="pagination-list"> */}
                        {/* This ul is required to keep the Next, Previous button together */}
                        {/* </ul> */}
                      </nav>
                    </Columns.Column>
                  </Columns>
                </Form>
              )}
            </Formik>
          </Columns.Column>
        </Columns>
      </Section>
      <Jobs jobs={jobs} />
      <Section>
        <Hero color={COLORS.LIGHT} size={"medium"} gradient>
          <Hero.Body align={"center"}>
            <Container>
              <Heading>
                Don't see your{" "}
                <span className="has-text-danger">Dream Job</span> here?
              </Heading>
              <Columns>
                <Columns.Column size={6} offset={3}>
                  <Heading subtitle>
                    There is a perfect job for everyone. If it doesn't exist
                    yet, it's often possible to create it. Describe your dream
                    job to us. We'll connect you to relevant companies in our
                    network.
                  </Heading>
                </Columns.Column>
              </Columns>
              <Level>
                <Level.Item>
                  <a
                    href={"https://forms.gle/BCDrU9SmweFzpxrM7"}
                    className="button is-dark is-large"
                  >
                    Tell us&nbsp;
                    <Icon icon="arrow-right" size="small" />
                  </a>
                </Level.Item>
              </Level>
            </Container>
          </Hero.Body>
        </Hero>
      </Section>
    </Fragment>
  );
};

// Index.getInitialProps = ctx => {
//   console.log("Index props called");
//   const variables = {
//     count: 5,
//     cursor: null
//   };
//   return {
//     variables
//   };
// };

export const getServerSideProps: GetServerSideProps = async ctx => {
  let {
    before = null,
    after = null,
    city = null,
    country = null,
    tags = null,
    company = null
  } = ctx.query;
  let absLast = null;
  let absFirst = null;
  let baseQuery = db
    .collection("jobs")
    .where("isActive", "==", true)
    .where("status", "==", PUBLISHED)
    .where("expiry", ">", Timestamp.now());

  if (city) {
    baseQuery = baseQuery.where("city", "==", city);
  }

  if (country) {
    baseQuery = baseQuery.where("country", "==", country);
  }

  if (tags) {
    if (Array.isArray(tags)) {
      baseQuery = baseQuery.where("tags", "array-contains", tags);
    } else {
      baseQuery = baseQuery.where("tags", "array-contains", tags);
    }
  }

  if (company) {
    baseQuery = baseQuery.where("company.id", "==", company);
  }

  let query = baseQuery.orderBy("expiry", "asc");
  let reverseQuery = baseQuery.orderBy("expiry", "desc");

  const absFirstData = await query.limit(1).get();
  if (!absFirstData.empty) {
    absFirst = absFirstData.docs[0]
      .data()
      .expiry.toDate()
      .toJSON();
  }

  const absLastData = await reverseQuery.limit(1).get();
  if (!absLastData.empty) {
    absLast = absLastData.docs[0]
      .data()
      .expiry.toDate()
      .toJSON();
  }

  if (after) {
    if (Array.isArray(after)) {
      after = after[after.length - 1];
    }
    const parsedStart = Timestamp.fromMillis(parseInt(after));
    query = query.startAfter(parsedStart).limit(JOBS_PAGE_SIZE);
  } else if (before) {
    if (Array.isArray(before)) {
      before = before[before.length - 1];
    }
    const parsedEnd = Timestamp.fromMillis(parseInt(before));
    query = query.endBefore(parsedEnd).limitToLast(JOBS_PAGE_SIZE);
  } else {
    query = query.limit(JOBS_PAGE_SIZE);
  }

  const snapshot = await query.get();

  let jobs = null;
  if (!snapshot.empty) {
    snapshot.size;
    jobs = snapshot.docs.map(docSnapshot => {
      const docData = docSnapshot.data();
      return {
        ...docData,
        id: docSnapshot.id,
        expiry: docData.expiry.toDate().toJSON(),
        created: docData?.created?.toDate().toJSON(),
        updated: docData?.updated?.toDate().toJSON()
      };
    });
  }

  const selectedCompany =
    company &&
    !Array.isArray(company) &&
    (
      await db
        .collection("companies")
        .doc(company)
        .get()
    ).data();

  return {
    props: {
      jobs,
      after,
      before,
      absLast,
      absFirst,
      selectedCompany: {
        name: selectedCompany?.name || null
      }
    }
  };
};

export default JobsIndex;
